import BigNumber from "bignumber.js";

/**
 * Remove trailing '0' runs and an optional trailing '.' from a numeric string.
 *
 * Replaces the regex `/\.?0+$/` which Sonar S5852 flags as a ReDoS risk
 * (false positive for this anchored pattern, but avoided entirely here).
 * Linear, backtracking-free; operates on char codes ('0' = 48, '.' = 46).
 */
function stripTrailingZerosAndDot(s: string): string {
  let end = s.length;
  while (end > 0 && s.charCodeAt(end - 1) === 48) end--;
  if (end > 0 && s.charCodeAt(end - 1) === 46) end--;
  return s.slice(0, end);
}

export function formatWei(value: string, decimals: number): string {
  if (!value || value === "0") return "0.00";
  const bn = new BigNumber(value).shiftedBy(-decimals);
  const dp = bn.isGreaterThanOrEqualTo(1) ? 2 : 4;
  return bn.toFormat(dp, BigNumber.ROUND_DOWN);
}

export function shortenAddress(address: string): string {
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function formatNumber(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatPrice(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num) || num === 0) return "$0.00";
  const decimals = num >= 1 ? 2 : 4;
  const factor = Math.pow(10, decimals);
  const truncated = Math.floor(num * factor) / factor;
  return `$${truncated.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

export function formatTokenValue(
  rawQuantity: string,
  tokenDecimals: number,
  price: string,
): string {
  const bn = new BigNumber(rawQuantity).shiftedBy(-tokenDecimals);
  const value = bn.multipliedBy(new BigNumber(price));
  if (value.isZero() || value.isNaN()) return "$0.00";
  const truncated = value
    .integerValue(BigNumber.ROUND_DOWN)
    .isGreaterThanOrEqualTo(1)
    ? value.decimalPlaces(2, BigNumber.ROUND_DOWN)
    : value.decimalPlaces(4, BigNumber.ROUND_DOWN);
  return `$${truncated.toFormat()}`;
}

/**
 * 전체 자산 합계 등 큰 숫자에 적용하는 표기 규칙.
 * - 정수부 ≥ 1: 천단위 구분 + 소수 2자리 절사
 * - 0 < x < 1 & 소수 4자리 유효 숫자 존재: 4자리 절사
 * - 0 < x < 1 & 소수 4자리까지 전부 0: "0.0001" + isLowerBound=true
 */
export function formatTokenValueForDisplay(
  val: string | number,
): { displayValue: string; isLowerBound: boolean } {
  if (val === "" || val === null || val === undefined) {
    return { displayValue: "0", isLowerBound: false };
  }

  const raw = typeof val === "number" ? val.toString() : val;
  if (raw === "0" || raw === "0.0") {
    return { displayValue: "0", isLowerBound: false };
  }

  let newValue = raw;
  if (newValue.includes("e") || newValue.includes("E")) {
    const num = Number(newValue);
    if (!Number.isFinite(num) || num === 0) {
      return { displayValue: "0", isLowerBound: false };
    }
    newValue = stripTrailingZerosAndDot(num.toFixed(18));
  }

  const [integerPart = "0", fractionPart = ""] = newValue.split(".");

  if (Number(integerPart) >= 1) {
    const frac2 = fractionPart.slice(0, 2).padEnd(2, "0");
    return {
      displayValue: `${Number(integerPart).toLocaleString("en-US")}.${frac2}`,
      isLowerBound: false,
    };
  }

  if (fractionPart.length < 4) {
    return {
      displayValue: `${integerPart}.${fractionPart}`,
      isLowerBound: false,
    };
  }

  const isTooSmall = fractionPart.slice(0, 4) === "0000";
  if (isTooSmall) {
    return { displayValue: "0.0001", isLowerBound: true };
  }

  return {
    displayValue: `0.${fractionPart.slice(0, 4)}`,
    isLowerBound: false,
  };
}
